import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { fmtDate } from "../../lib/format";

export default function AdminStaff() {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "ra", mobile: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/admin/staff", form);
      toast.success(`${form.role.toUpperCase()} '${form.name}' created. Share the password with them.`);
      setForm({ name: "", email: "", password: "", role: form.role, mobile: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const resetPwd = async (u) => {
    const np = window.prompt(`Set a new password for ${u.email} (≥ 8 chars):`, "");
    if (!np) return;
    try {
      await api.post(`/admin/users/${u.user_id}/reset-password`, { new_password: np });
      toast.success(`Password reset for ${u.email}. Share it with them.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const roleBadge = (role) => {
    const map = {
      admin: "bg-zinc-900 text-white border-zinc-900",
      ra: "bg-blue-50 text-blue-700 border-blue-200",
      crm: "bg-purple-50 text-purple-700 border-purple-200",
    };
    return map[role] || "bg-zinc-50 text-zinc-700 border-zinc-200";
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Operations</div>
        <h1 className="font-display font-bold text-3xl text-zinc-950 mt-1">Staff Management</h1>
        <p className="text-sm text-zinc-500 mt-1">Create RA and CRM accounts. Reset any user's password (staff or customer) from this page or a client's detail page.</p>
      </div>

      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-6">
          <h3 className="font-display font-semibold text-zinc-950 mb-4">Create new staff</h3>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ra">RA — Research Analyst</SelectItem>
                  <SelectItem value="crm">CRM</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Name</Label><Input data-testid="staff-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div className="md:col-span-2"><Label>Email</Label><Input data-testid="staff-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 font-mono" /></div>
            <div><Label>Mobile (optional)</Label><Input data-testid="staff-mobile-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="mt-1 font-mono" /></div>
            <div><Label>Password (≥ 8)</Label><Input data-testid="staff-password-input" required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 font-mono" /></div>
            <div className="md:col-span-6">
              <Button data-testid="create-staff-btn" type="submit" disabled={busy} className="bg-zinc-950 hover:bg-zinc-800 text-white">
                {busy ? "Creating..." : "Create staff account"}
              </Button>
              <span className="ml-3 text-xs text-zinc-500">Password is shown in plain text intentionally — copy it before saving.</span>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-0">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div className="font-display font-semibold text-zinc-950">All staff</div>
            <Badge className="bg-zinc-50 text-zinc-700 border-zinc-200">{staff.length}</Badge>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead className="text-right">Created</TableHead>
                <TableHead className="text-right w-32">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staff.length === 0 && <TableRow><TableCell colSpan={6} className="text-zinc-500">No staff yet.</TableCell></TableRow>}
              {staff.map((u) => (
                <TableRow key={u.user_id} data-testid={`staff-row-${u.user_id}`}>
                  <TableCell><Badge className={roleBadge(u.role)}>{u.role.toUpperCase()}</Badge></TableCell>
                  <TableCell className="font-display font-semibold">{u.name || "—"}</TableCell>
                  <TableCell className="font-mono text-sm text-zinc-700">{u.email}</TableCell>
                  <TableCell className="font-mono text-sm">{u.mobile || "—"}</TableCell>
                  <TableCell className="text-right text-xs font-mono text-zinc-500">{fmtDate(u.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`reset-pwd-${u.user_id}`} size="sm" variant="outline" onClick={() => resetPwd(u)} className="h-7 text-xs">Reset password</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
